<script lang="ts">
  import { get } from 'svelte/store'
  import { vaultStore } from '$lib/stores/vaultStore'
  import ImageSizeMenu from '$components/ImageSizeMenu.svelte'
  import templateImages from '../../template-images.json' assert { type: 'json' }

  interface Props {
    imagePath: string
    title?: string
    onDelete?: () => void
  }

  let { imagePath, title = 'Image', onDelete }: Props = $props()

  let imageUrl = $state<string>('')
  let imageData = $state<string>('')
  let loading = $state(true)
  let error = $state(false)
  let refreshKey = $state(0)

  // R2 CDN domain for template images
  const R2_DOMAIN = 'https://korean-grammar-images.junlim.org'
  const TEMPLATE_IMAGES = (templateImages as string[])
  const TEMPLATE_IMAGE_SET = new Set(TEMPLATE_IMAGES)

  $effect(() => {
    loadImage()
  })

  async function loadImage() {
    loading = true
    error = false

    try {
      const vault = get(vaultStore)
      const isElectron = typeof window !== 'undefined' && (window as any).electronAPI
      const isDemoMode = vault?.kind === 'demo' || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === 'true')

      // In demo mode, use R2 CDN for template images
      if (isDemoMode) {
        try {
          // Decode the image path to handle Korean characters
          const decodedPath = decodeURIComponent(imagePath)

          // Extract filename from path (handles both "이미지/file.png" and "file.png")
          const filename = decodedPath.split('/').pop() || decodedPath
          let normalizedFilename = filename
          try {
            normalizedFilename = decodeURIComponent(filename)
          } catch {
            // ignore decoding issues and use raw filename
          }

          // Check if this is a template image that should be served from R2
          if (TEMPLATE_IMAGE_SET.has(normalizedFilename)) {
            const encodedFilename = encodeURIComponent(normalizedFilename)
            // Load from R2 CDN for fast loading (encode spaces and non-ASCII)
            imageUrl = `${R2_DOMAIN}/${encodedFilename}`
            console.log(`Loading template image from R2: ${imageUrl}`)
          } else {
            // For other images in demo mode, this shouldn't happen
            // but fallback to error state
            console.warn(`Unknown image in demo mode: ${filename}`)
            error = true
          }
        } catch (err) {
          console.error('Failed to load image from R2:', imagePath, err)
          error = true
        }

        loading = false
        return
      }

      if (isElectron && (window as any).electronAPI?.readImage) {
        // Use Electron IPC to read the image file
        try {
          // Decode URL-encoded path (e.g., %EC%9D%B4%EB%AF%B8%EC%A7%80 -> 이미지)
          const decodedPath = decodeURIComponent(imagePath)

          console.log('[ImageViewer] Original path:', imagePath)
          console.log('[ImageViewer] Decoded path:', decodedPath)
          console.log('[ImageViewer] Vault path:', vault.vaultPath)
          console.log('[ImageViewer] Vault initialized:', vault.isInitialized)

          const fullPath = vault.vaultPath
            ? `${vault.vaultPath}/${decodedPath}`
            : decodedPath

          console.log('[ImageViewer] Full path:', fullPath)

          // Read file content via IPC
          const result = await (window as any).electronAPI.readImage(fullPath)
          
          if (result.success && result.content) {
            // Convert to base64 data URL
            // The content is already base64 encoded from the backend
            const extension = imagePath.split('.').pop()?.toLowerCase()
            const mimeType = {
              'png': 'image/png',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'gif': 'image/gif',
              'webp': 'image/webp',
              'svg': 'image/svg+xml',
              'bmp': 'image/bmp'
            }[extension || 'png'] || 'image/png'
            
            imageUrl = `data:${mimeType};base64,${result.content}`
          } else {
            throw new Error('Failed to read image file')
          }
        } catch (err) {
          console.error('Failed to read image via IPC:', err)
          // Fallback to file:// URL with decoded path
          const decodedPath = decodeURIComponent(imagePath)
          if (vault.vaultPath) {
            imageUrl = `file://${vault.vaultPath}/${decodedPath}`
          } else {
            imageUrl = `file://${decodedPath}`
          }
        }
      } else if (vault.vaultPath) {
        // Use file:// protocol with full path (decode URL-encoded paths)
        const decodedPath = decodeURIComponent(imagePath)
        imageUrl = `file://${vault.vaultPath}/${decodedPath}`
      } else {
        // Fallback to relative path (decode URL-encoded paths)
        imageUrl = decodeURIComponent(imagePath)
      }
    } catch (err) {
      console.error('Failed to load image:', err)
      error = true
    } finally {
      loading = false
    }
  }
</script>

<div class="image-viewer">
  {#if loading}
    <div class="loading">
      <div class="spinner"></div>
      <p>이미지 로딩 중...</p>
    </div>
  {:else if error}
    <div class="error">
      <p>이미지를 불러올 수 없습니다</p>
      <p class="path">{imagePath}</p>
    </div>
  {:else}
    <div class="image-container">
      <div class="image-wrapper">
        <img
          key={refreshKey}
          src={imageUrl}
          alt={title}
        />
        <ImageSizeMenu
          {imagePath}
          onResizeComplete={() => {
            // Force reload image
            refreshKey++
            loadImage()
          }}
          {onDelete}
        />
      </div>
    </div>
  {/if}
</div>

<style>
  .image-viewer {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-primary);
    overflow: auto;
  }
  
  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: var(--text-secondary);
  }
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-primary);
    border-top-color: var(--accent-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .error {
    text-align: center;
    color: var(--text-secondary);
  }
  
  .error .path {
    margin-top: 8px;
    font-size: var(--ui-font-size-sm);
    color: var(--text-tertiary);
    font-family: monospace;
  }
  
  .image-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px;
    box-sizing: border-box;
  }

  .image-wrapper {
    position: relative;
    display: inline-block;
    line-height: 0;
  }

  .image-wrapper:hover :global(.image-size-menu .menu-button) {
    opacity: 1;
  }

  .image-container img {
    max-width: calc(100vw - 120px);
    max-height: calc(100vh - 120px);
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    display: block;
  }
</style>
